<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="competitorTypes"
      :headers="competitorTypesHeaders"
      :list="competitorTypes"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNewCompetitorType"
      @refresh-data="refreshData"
      table-id="competitor-types"
      clickable
      @table-item-clicked="onEditCompetitorType"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as CompetitorType)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <CompetitorTypeModalCreateEdit
      v-if="createEditModalActive"
      :competitor-type="competitorType"
      :title="modalEditCreateTitle()"
      :mode="createEditModalMode"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { getCompetitorTypes, removeCompetitorTypesByIds } from '@/services/api/traders';
import { filterActive, filterData, sortData } from '@/utils/table';
import { v4 as uuidv4 } from 'uuid';

import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import CompetitorTypeModalCreateEdit from '@/components/traders/CompetitorTypeModalCreateEdit.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { type CompetitorType } from '@/types/Traders';
import type { TableFilterData } from '@/types/Base';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();
const competitorTypes = ref<CompetitorType[]>();
const filterIsActive = ref<boolean | undefined>(undefined);

const { reloadLookups } = useLookupsStore();
const competitorTypesHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  competitorTypes.value = (await getCompetitorTypes()) as CompetitorType[];
});

function initializeCompetitorType(): CompetitorType {
  return {
    id: uuidv4(),
    code: '',
    name: '',
    isActive: true,
    isFromManager: true,
    deleted: false,
  };
}

const competitorType = ref<CompetitorType>(initializeCompetitorType());
const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

function onAddNewCompetitorType() {
  competitorType.value = initializeCompetitorType();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEditCompetitorType(item: CompetitorType) {
  competitorType.value = item;
  createEditModalActive.value = true;
  createEditModalMode.value = 'edit';
}

async function onSave(modifiedData: CompetitorType) {
  if (!competitorTypes.value) return;

  const index = competitorTypes.value.findIndex((item) => item.id === modifiedData.id);
  if (index !== -1) competitorTypes.value[index] = modifiedData;
  else competitorTypes.value.push(modifiedData);
  createEditModalActive.value = false;

  await reloadLookups(['competitorTypes']);
}

async function refreshData(data: TableFilterData) {
  let response = await getCompetitorTypes();

  if (data.query) response = filterData(response, data.query, ['name', 'code']);

  if (data.column !== undefined && data.order)
    response = sortData(response, data.column, data.order, competitorTypesHeaders);

  if (data.isActive !== undefined) response = filterActive(response, data.isActive);

  competitorTypes.value = response;
}

async function onDeleteMultiselect(items: CompetitorType[]) {
  const idsToDelete = items.map((item) => item.id);
  const res = await removeCompetitorTypesByIds(idsToDelete);
  if (!res && competitorTypes.value)
    competitorTypes.value = competitorTypes.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['competitorTypes']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeCompetitorTypesByIds([idToDelete]);
  if (!res && competitorTypes.value)
    competitorTypes.value = competitorTypes.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['competitorTypes']);
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<CompetitorType>();
function onDeleteRow(item: CompetitorType) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (competitorType.value && !competitorType.value.isFromManager)
    return `${$t('common.actions.view')}`;
  return createEditModalMode.value === 'edit'
    ? `${$t('common.actions.edit')}`
    : `${$t('common.actions.create')}`;
}
function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
